import { useState } from 'react';
import { Button, Card, Center, Group, Loader, Select, SimpleGrid, Stack, Switch, Title, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { useMembers, useOrganization, useUpdateOrganization, type Organization, type OrganizationPatch } from '@/api/orgs';
import { useProjects } from '@/api/projects';
import { AddMemberModal } from '@/features/orgs/AddMemberModal';
import { EmptyState } from '@/components/EmptyState';
import { StaffBreadcrumbs } from '@/components/StaffBreadcrumbs';
import { usePageTitle } from '@/components/usePageTitle';
import { MembersTable } from '@/features/orgs/MembersTable';
import { CreateProjectModal } from '@/features/projects/CreateProjectModal';
import { ImportProjectModal } from '@/features/staff/ImportProjectModal';
import { OrganizationCostCard } from '@/features/staff/OrganizationCostCard';
import { ProjectsTable } from '@/features/staff/ProjectsTable';
import { LOCALES, LOCALE_LABELS, type Locale } from '@/i18n';

export const Route = createFileRoute('/_staff/staff/organizations/$orgId')({ component: OrganizationDetailPage });

function OrganizationDetailPage() {
  const { t } = useTranslation();
  const { orgId } = Route.useParams();
  const { data: org, isPending, isError, error } = useOrganization(orgId);
  const { data: members, isPending: membersPending } = useMembers(orgId);
  const [addOpen, setAddOpen] = useState(false);
  usePageTitle(org?.name ?? t('nav.organizations'));

  if (isPending) {
    return (
      <Center py="xl">
        <Loader color="forest" />
      </Center>
    );
  }
  if (isError || !org) return <EmptyState title={errorMessage(error)} />;

  return (
    <Stack gap="lg">
      <StaffBreadcrumbs organization={org} />
      <Title order={1}>{org.name}</Title>
      <OrganizationProjects org={org} />
      <OrganizationCostCard orgId={orgId} />
      <OrganizationFieldsCard key={org.public_id} org={org} orgId={orgId} />
      <Group justify="space-between" align="center">
        <Title order={2}>{t('org.members')}</Title>
        <Button onClick={() => setAddOpen(true)}>{t('org.addMember')}</Button>
      </Group>
      {membersPending ? (
        <Center py="xl">
          <Loader color="forest" />
        </Center>
      ) : (
        <MembersTable orgId={orgId} members={members ?? []} canManage />
      )}
      <AddMemberModal orgId={orgId} opened={addOpen} onClose={() => setAddOpen(false)} />
    </Stack>
  );
}

/** The organization's projects with the same controls the staff projects list has, scoped to this organization. */
function OrganizationProjects({ org }: { org: Organization }) {
  const { t } = useTranslation('projects');
  const navigate = useNavigate();
  const [archived, setArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const projects = useProjects({ organization: org.public_id, includeArchived: archived });
  return (
    <>
      <Group justify="space-between" align="center">
        <Group gap="lg">
          <Title order={2}>{t('staff.projects')}</Title>
          <Switch label={t('list.showArchived')} checked={archived} onChange={(e) => setArchived(e.currentTarget.checked)} />
        </Group>
        <Group gap="sm">
          <Button variant="default" onClick={() => setImportOpen(true)}>
            {t('staff.import.button')}
          </Button>
          <Button onClick={() => setCreateOpen(true)}>{t('staff.newProject')}</Button>
        </Group>
      </Group>
      {projects.isPending ? (
        <Center py="xl">
          <Loader color="forest" />
        </Center>
      ) : projects.isError ? (
        <EmptyState title={errorMessage(projects.error)} />
      ) : projects.data.length === 0 ? (
        <EmptyState title={t('list.empty')} />
      ) : (
        <ProjectsTable projects={projects.data} hideOrganization />
      )}
      <CreateProjectModal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        organizations={[org]}
        defaultCrs={org.default_crs}
        showInternal
        navigateTo={(id) => void navigate({ to: '/staff/projects/$projectId', params: { projectId: id } })}
      />
      <ImportProjectModal
        organizationId={org.public_id}
        opened={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={(id) => void navigate({ to: '/staff/projects/$projectId', params: { projectId: id } })}
      />
    </>
  );
}

function OrganizationFieldsCard({ org, orgId }: { org: Organization; orgId: string }) {
  const { t } = useTranslation();
  const updateOrganization = useUpdateOrganization(orgId);
  const [name, setName] = useState(org.name);
  const [defaultCrs, setDefaultCrs] = useState(org.default_crs ?? '');
  const [defaultLocale, setDefaultLocale] = useState<Locale>(org.default_locale ?? 'cs');

  const handleSave = async () => {
    const patch: OrganizationPatch = { name, default_crs: defaultCrs, default_locale: defaultLocale };
    try {
      await updateOrganization.mutateAsync(patch);
      notifications.show({ color: 'forest', message: t('common.save') });
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };

  return (
    <Card>
      <Stack gap="md">
        <SimpleGrid cols={2}>
          <TextInput label={t('org.name')} value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <TextInput label={t('org.defaultCrs')} value={defaultCrs} onChange={(e) => setDefaultCrs(e.currentTarget.value)} />
          <Select
            label={t('org.defaultLocale')}
            data={LOCALES.map((l) => ({ value: l, label: LOCALE_LABELS[l] }))}
            value={defaultLocale}
            allowDeselect={false}
            onChange={(v) => v && setDefaultLocale(v as Locale)}
          />
        </SimpleGrid>
        <Group justify="flex-end">
          <Button onClick={() => void handleSave()} loading={updateOrganization.isPending}>
            {t('common.save')}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
